import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { useOrganization } from '@/contexts/OrganizationContext'
import type { Category } from '@/lib/supabase'
import { keepPreviousForOrg } from '@/lib/query-keys'

/**
 * Query keys for category data
 * All keys include organizationId to ensure proper cache isolation
 */
const CATEGORY_KEYS = {
  list: (organizationId: string | undefined, page?: number, pageSize?: number, typeFilter?: string) =>
    ['categories', organizationId, page, pageSize, typeFilter] as const,
}

/**
 * Hook to fetch categories for the current organization with pagination and optional type filter.
 * Uses tenant-specific database client — no organizationId filtering in queries.
 * @param page - 1-indexed page number (default: 1)
 * @param pageSize - Items per page (default: 25)
 * @param typeFilter - Filter by category type: 'all', 'inflow', or 'outflow' (default: 'all')
 */
export function useCategories(page: number = 1, pageSize: number = 25, typeFilter: string = 'all') {
  const { currentOrganization } = useOrganization()

  return useQuery<{ items: Category[]; total: number }>({
    queryKey: CATEGORY_KEYS.list(currentOrganization?.id, page, pageSize, typeFilter),
    queryFn: async () => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.categories.list({
        page,
        pageSize,
        type: typeFilter !== 'all' ? (typeFilter as 'inflow' | 'outflow') : undefined,
      })

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    enabled: !!currentOrganization?.id,
    // The total travels with each page; keep it while the next page loads.
    placeholderData: keepPreviousForOrg(currentOrganization?.id, 1),
  })
}

/**
 * Hook to create a new category.
 * Creates a category in the tenant schema — no organization_id needed.
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (input: {
      name: string
      // Optional after DCP-250: categories.type is nullable and the create form
      // no longer collects it — the inflow/outflow sign comes from the amount value.
      type?: 'inflow' | 'outflow'
      description?: string | null
      sage_gl_account?: string | null
      default_priority?: boolean
    }) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.categories.create({
        name: input.name,
        type: input.type,
        description: input.description,
        sage_gl_account: input.sage_gl_account,
        default_priority: input.default_priority,
      })

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    onSuccess: () => {
      // Invalidate all category queries globally — covers both useCategories key shapes
      // (settings page uses ['categories', orgId, ...] and projections uses ['categories', 'list', orgId])
      queryClient.invalidateQueries({
        queryKey: ['categories']
      })
      toast.success('Category created successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create category')
    },
  })
}

/**
 * Hook to update an existing category.
 * Updates category details by ID within the tenant schema.
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: {
      id: string
      name?: string
      type?: 'inflow' | 'outflow'
      description?: string | null
      sage_gl_account?: string | null
      is_active?: boolean
      default_priority?: boolean
    }) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const { id, ...updateData } = params
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.categories.update(id, updateData)

      if (error) {
        throw new Error(error.message)
      }

      return data
    },
    onSuccess: () => {
      // Invalidate all category queries globally — covers both useCategories key shapes
      queryClient.invalidateQueries({
        queryKey: ['categories']
      })
      toast.success('Category updated successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update category')
    },
  })
}

/**
 * Hook to delete a category.
 * Deletes a category by ID within the tenant schema.
 * Note: Will fail if category has associated transactions due to FK constraints
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (id: string) => {
      // Import createTenantDb here to avoid circular dependency
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.categories.delete(id)

      if (error) {
        // Provide user-friendly error for constraint violations
        if (error.code === '23503') {
          throw new Error('Cannot delete category: it is being used by existing transactions')
        }
        throw new Error(error.message)
      }

      return null
    },
    onSuccess: () => {
      // Invalidate all category queries globally — covers both useCategories key shapes
      queryClient.invalidateQueries({
        queryKey: ['categories']
      })
      toast.success('Category deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete category')
    },
  })
}
